import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Autocomplete,
  Switch,
  FormControlLabel,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  AlertTitle,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Launch as LaunchIcon,
  Preview as PreviewIcon,
  Calculate as CalculateIcon,
  ExpandMore as ExpandMoreIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
} from '@mui/icons-material';
// Date picker imports temporarily disabled due to compatibility issues
// import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
// import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
// import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useForm, Controller } from 'react-hook-form';
import toast from 'react-hot-toast';

import LoadingSpinner from '../../components/Common/LoadingSpinner';
import RichTextEditor from '../../components/Common/RichTextEditor';
import CampaignTimeline from '../../components/CampaignTimeline';
import { CampaignProductSelection } from '../../components/Campaigns';
import BimesterProductSelector from '../../components/Campaigns/BimesterProductSelector';
import TierProductionConfig from '../../components/Campaigns/TierProductionConfig';
import { fetchBrandList } from '../../api/brands';
import { getTemplateForCategory, calculateTierCost } from '../../config/productionTemplates';

const FALLBACK_BRANDS = [
  { id: 'demo-brand', name: 'Demo Brand', slug: 'demo-brand' },
  { id: 'urban-threads', name: 'Urban Threads Collective', slug: 'urban-threads' },
  { id: 'atelier-nova', name: 'Atelier Nova', slug: 'atelier-nova' },
];

const steps = [
  'Basic Information',
  'Select Products from Bimester',
  'Configure Products',
  'Campaign Content',
  'Preview & Launch'
];

const CampaignBuilderPage = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(campaignId);
  
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [brandOptions, setBrandOptions] = useState([]);
  const [brandLoading, setBrandLoading] = useState(false);
  const [brandError, setBrandError] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState(null);

  // Multi-product campaign state
  const [selectedBimester, setSelectedBimester] = useState(null);
  const [selectedLooks, setSelectedLooks] = useState([]); // Array of selected looks (products)
  const [currentProductIndex, setCurrentProductIndex] = useState(0); // Which product we're configuring
  const [productConfigurations, setProductConfigurations] = useState({}); // Store config for each product by lookId

  // Tier production configuration state
  const [tierProductionConfigs, setTierProductionConfigs] = useState({}); // Production config per tier (1, 2, 3)
  const [productionTemplate, setProductionTemplate] = useState(null); // Auto-populated template

  // Enhanced pricing calculator state
  // Price band presets for different product categories
  const priceBandPresets = [
    { label: 'Budget/Streetwear ($20-100)', value: '19,29,39,49,59,69,79,89,99' },
    { label: 'Mid-Range ($50-200)', value: '49,59,69,79,89,99,109,119,129,139,149,159,169,179,189' },
    { label: 'Premium ($100-300)', value: '99,109,119,129,139,149,159,169,179,189,199,219,239,259,279' },
    { label: 'Luxury ($200-500)', value: '199,219,239,259,279,299,319,339,359,379,399,419,439,459,479' },
    { label: 'High-End ($400+)', value: '399,449,499,549,599,649,699,749,799,849,899,949,999' },
    { label: 'Custom', value: 'custom' }
  ];

  const [pricingData, setPricingData] = useState({
    cost: '',
    targetMargin: '',
    priceBands: '49,59,69,79,89,99,109,119,129,139,149,159,169,179,189',
    leadTimeDays: '21',
    shipDays: '7',
    // Flexible tier configuration (2-6 tiers)
    tierCount: 4, // Default to 4 tiers for backwards compatibility
    tierNames: ['Express', 'Standard', 'Early Bird', 'Pre-Order'],
    expectedTierDistribution: [0.075, 0.20, 0.35, 0.375], // Default 4-tier distribution
    maxReservationTarget: '500',
    minMur: '45',
    inventoryBufferUnits: '0',
    expediteDays: '0'
  });
  const [pricingMode, setPricingMode] = useState('margin'); // 'margin' or 'markup'
  const [markupMultiplier, setMarkupMultiplier] = useState('2.5');
  const [selectedPriceBandPreset, setSelectedPriceBandPreset] = useState('Mid-Range ($50-200)');
  const [customPriceBands, setCustomPriceBands] = useState('');
  const [calculatedPricing, setCalculatedPricing] = useState(null);
  const [calculatingPricing, setCalculatingPricing] = useState(false);

  // Markup bands overlay state
  const [markupBands, setMarkupBands] = useState('1.4,1.6,1.8,2.0');
  const [markupOverlay, setMarkupOverlay] = useState(null);
  const [showMarkupOverlay, setShowMarkupOverlay] = useState(false);

  // Tier selection state
  const [selectedTiers, setSelectedTiers] = useState(new Set());
  const [availableTiers, setAvailableTiers] = useState([]);

  // Expandable tier rows state
  const [expandedTiers, setExpandedTiers] = useState(new Set());
  const [tierConfigurations, setTierConfigurations] = useState({});

  // Automated tier date handling state (reservation period)
  const [campaignStartDate, setCampaignStartDate] = useState(new Date());
  const [customWaitOffsets, setCustomWaitOffsets] = useState({});
  const [sharedStartDateMode, setSharedStartDateMode] = useState(true); // true = shared, false = independent

  // Production configuration (matches new Campaign schema)
  const [productionConfig, setProductionConfig] = useState({
    leadTimeDays: 21,
    shippingDays: 7,
    expediteDays: 0,
    inventoryBufferUnits: 0
  });
  
  const { control, handleSubmit, watch, setValue, formState: { errors } } = useForm({
    defaultValues: {
      brandId: '',
      name: '',
      description: '',
      inventory: {
        initialQuantity: '',
        safetyStock: '',
        restockLeadTimeDays: '',
        oversellBuffer: '',
      },
      sivadProduct: null,
      content: {
        storyTitle: '',
        storyContent: '',
        designProcess: '',
        lifestylePhilosophy: '',
        stylingNotes: '',
        targetCustomer: '',
        productionTimeline: '',
        heroMedia: '',
        socialProof: ''
      },
      settings: {
        autoAdvanceTiers: true,
        notifyOnMilestones: true,
        allowBackorders: false,
        maxReservationsPerCustomer: 5
      }
    }
  });
  const brandReady = isEditing || Boolean(selectedBrand);
  const brandIdValue = watch('brandId');

  /**
   * Calculate required margin to hit revenue target
   *
   * Logic:
   * 1. Required Avg Price = Revenue Target / Units
   * 2. Calculate tier discounts from plannedDiscount + distribution
   * 3. Discount Factor = Σ((1 - tier_discount[i]) × distribution[i])
   * 4. Base Retail = Required Avg Price / Discount Factor
   * 5. Required Margin = (Base Retail - Cost) / Base Retail
   */
  const calculateRequiredMargin = (planningData) => {
    const {
      revenuePlanning,
      estimatedCost,
      units,
      plannedDiscount,
      expectedTierDistribution
    } = planningData;

    // Validate inputs
    if (!revenuePlanning?.lookRevenueTarget || !units || !estimatedCost || units === 0) {
      return null;
    }

    const revenueTarget = revenuePlanning.lookRevenueTarget;
    const cost = estimatedCost;

    // Step 1: Required average selling price
    const requiredAvgPrice = revenueTarget / units;

    // Step 2: Calculate tier discounts using proportional formula
    // Tier 1 (Express/fastest tier) always 0%, others proportional
    const tierCount = expectedTierDistribution.length;
    const tierDiscounts = [0]; // First tier (fastest)

    if (plannedDiscount && expectedTierDistribution) {
      // Calculate sum of squared distributions for non-fastest tiers
      let sumOfSquared = 0;
      for (let i = 1; i < tierCount; i++) {
        sumOfSquared += Math.pow(expectedTierDistribution[i], 2);
      }

      if (sumOfSquared > 0) {
        const k = plannedDiscount / sumOfSquared;
        for (let i = 1; i < tierCount; i++) {
          tierDiscounts.push(k * expectedTierDistribution[i]);
        }
      } else {
        // Fill remaining tiers with 0
        for (let i = 1; i < tierCount; i++) {
          tierDiscounts.push(0);
        }
      }
    } else {
      // Fill remaining tiers with 0
      for (let i = 1; i < tierCount; i++) {
        tierDiscounts.push(0);
      }
    }

    // Step 3: Calculate discount factor (weighted average of price retention)
    const discountFactor = expectedTierDistribution.reduce(
      (sum, dist, i) => sum + dist * (1 - tierDiscounts[i]),
      0
    );

    if (discountFactor === 0) {
      return null;
    }

    // Step 4: Reverse-calculate base retail price
    const baseRetail = requiredAvgPrice / discountFactor;

    // Step 5: Calculate required margin
    const requiredMargin = (baseRetail - cost) / baseRetail;

    console.log('📐 Required margin calculation:', {
      revenueTarget: `$${revenueTarget.toFixed(2)}`,
      units,
      cost: `$${cost.toFixed(2)}`,
      requiredAvgPrice: `$${requiredAvgPrice.toFixed(2)}`,
      tierDiscounts: tierDiscounts.map(d => `${(d * 100).toFixed(1)}%`),
      discountFactor: discountFactor.toFixed(4),
      baseRetail: `$${baseRetail.toFixed(2)}`,
      requiredMargin: `${(requiredMargin * 100).toFixed(1)}%`
    });

    return {
      requiredMargin,
      baseRetail,
      requiredAvgPrice,
      revenueTarget,
      tierDiscounts,
      discountFactor
    };
  };

  // Debug: Log pricingData tier configuration changes
  useEffect(() => {
    console.log('🔍 pricingData tier config changed:', {
      tierCount: pricingData.tierCount,
      tierNamesLength: pricingData.tierNames?.length,
      tierNames: pricingData.tierNames,
      expectedTierDistributionLength: pricingData.expectedTierDistribution?.length,
      expectedTierDistribution: pricingData.expectedTierDistribution
    });
  }, [pricingData.tierCount, pricingData.tierNames, pricingData.expectedTierDistribution]);

  // Sync production config with pricing data
  useEffect(() => {
    setProductionConfig({
      leadTimeDays: parseInt(pricingData.leadTimeDays) || 21,
      shippingDays: parseInt(pricingData.shipDays) || 7,
      expediteDays: parseInt(pricingData.expediteDays) || 0,
      inventoryBufferUnits: parseInt(pricingData.inventoryBufferUnits) || 0
    });
  }, [pricingData.leadTimeDays, pricingData.shipDays, pricingData.expediteDays, pricingData.inventoryBufferUnits]);

  // Pre-populate pricing data from selected look's planning data
  useEffect(() => {
    const formData = watch();
    const planningData = formData._planningData;

    if (planningData && planningData.estimatedCost) {
      console.log('📦 Pre-populating pricing calculator from look planning data:', {
        estimatedCost: planningData.estimatedCost,
        units: planningData.units,
        revenueTarget: planningData.revenuePlanning?.lookRevenueTarget
      });

      // Calculate required margin to hit revenue target
      const marginCalc = calculateRequiredMargin(planningData);

      setPricingData(prev => {
        const updates = {
          cost: prev.cost || planningData.estimatedCost?.toString() || '',
          maxReservationTarget: prev.maxReservationTarget === '500' ? (planningData.units?.toString() || '500') : prev.maxReservationTarget
        };

        // If we calculated a required margin, pre-populate it
        if (marginCalc && marginCalc.requiredMargin > 0) {
          updates.targetMargin = (marginCalc.requiredMargin * 100).toFixed(1);
          console.log(`✅ Auto-calculated target margin: ${updates.targetMargin}% to hit revenue target of $${marginCalc.revenueTarget.toFixed(2)}`);
        } else if (planningData.revenuePlanning?.annualMarginTarget) {
          // Fallback to annual margin target
          updates.targetMargin = (planningData.revenuePlanning.annualMarginTarget * 100).toFixed(1);
          console.log(`⚠️ Using annual margin target: ${updates.targetMargin}%`);
        }

        return {
          ...prev,
          ...updates
        };
      });
    }
  }, [watch('_planningData')]);

  // Update tier configurations when shared start date mode changes
  useEffect(() => {
    if (calculatedPricing?.calculation?.tiers && Object.keys(tierConfigurations).length > 0) {
      const updatedConfigs = {};

      calculatedPricing.calculation.tiers.forEach(tier => {
        if (tierConfigurations[tier.tierId]) {
          updatedConfigs[tier.tierId] = {
            ...tierConfigurations[tier.tierId],
            reservationStartDate: formatDateForInput(calculateTierStartDate(tier, campaignStartDate)),
            reservationEndDate: formatDateForInput(calculateTierEndDate(tier, campaignStartDate))
          };
        }
      });

      if (Object.keys(updatedConfigs).length > 0) {
        setTierConfigurations(prev => ({ ...prev, ...updatedConfigs }));
      }
    }
  }, [sharedStartDateMode, campaignStartDate]);


  useEffect(() => {
    const loadBrands = async () => {
      try {
        setBrandLoading(true);
        setBrandError(null);

        const response = await fetchBrandList({ limit: 100 });
        let brands = Array.isArray(response?.brands) ? response.brands : Array.isArray(response) ? response : [];

        if (!brands.length) {
          brands = [...FALLBACK_BRANDS];
          setBrandError('Using demo brands until your brand directory is connected.');
        } else {
          brands = brands.map((brand, index) => ({
            id: brand.id || brand._id || brand.slug || `brand-${index}`,
            name: brand.name,
            slug: brand.slug,
          }));
        }

        setBrandOptions(brands);
      } catch (error) {
        console.error('Failed to load brands:', error);
        setBrandError('Unable to load brands. Using demo brands instead.');
        setBrandOptions([...FALLBACK_BRANDS]);
      } finally {
        setBrandLoading(false);
      }
    };

    loadBrands();
  }, []);

  useEffect(() => {
    if (selectedBrand) {
      setValue('brandId', selectedBrand.id);
      setBrandOptions((prev) => {
        if (prev.some((brand) => brand.id === selectedBrand.id)) {
          return prev;
        }
        return [...prev, selectedBrand];
      });
    } else if (!isEditing) {
      setValue('brandId', '');
    }
  }, [selectedBrand, setValue, isEditing]);

  // Auto-populate pricing data from current product's planning data
  useEffect(() => {
    const currentProduct = getCurrentProduct();
    if (currentProduct && currentProduct.estimatedCost) {
      setPricingData(prev => ({
        ...prev,
        cost: currentProduct.estimatedCost.toString(),
        // Auto-populate max reservation target from planning units
        maxReservationTarget: currentProduct.units || undefined,
        // If planning has discount data, we can use it
        plannedDiscount: currentProduct.plannedDiscount || undefined,
        expectedTierDistribution: currentProduct.expectedTierDistribution || undefined
      }));
    }
  }, [currentProductIndex, selectedLooks]);

  // Auto-populate production template based on product category
  useEffect(() => {
    const currentProduct = getCurrentProduct();
    if (currentProduct && currentProduct.categoryLabel) {
      const template = getTemplateForCategory(currentProduct.categoryLabel);
      setProductionTemplate(template);

      // Auto-populate tier costs based on planning cost
      if (currentProduct.estimatedCost && template) {
        const initialConfigs = {};
        Object.entries(template.tiers).forEach(([tierNum, tierTemplate]) => {
          const tierNumber = parseInt(tierNum);
          initialConfigs[tierNumber] = {
            ...tierTemplate,
            tierNumber,
            costPerUnit: calculateTierCost(currentProduct.estimatedCost, tierNumber)
          };
        });
        setTierProductionConfigs(initialConfigs);
      }
    }
  }, [currentProductIndex, selectedLooks]);

  // Mock products - replace with real API call
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        // Mock product data
        const mockProducts = [
          {
            id: 'product_1',
            title: 'Summer Vibes Hoodie',
            handle: 'summer-vibes-hoodie',
            images: [{ url: 'https://example.com/hoodie.jpg' }],
            variants: [
              { id: 'variant_1', title: 'Small / Blue', price: 89.00, inventoryQuantity: 100, sku: 'SVH-S-BLU' },
              { id: 'variant_2', title: 'Medium / Blue', price: 89.00, inventoryQuantity: 150, sku: 'SVH-M-BLU' },
              { id: 'variant_3', title: 'Large / Blue', price: 89.00, inventoryQuantity: 120, sku: 'SVH-L-BLU' }
            ]
          },
          {
            id: 'product_2',
            title: 'Coastal Collection Tee',
            handle: 'coastal-collection-tee',
            images: [{ url: 'https://example.com/tee.jpg' }],
            variants: [
              { id: 'variant_4', title: 'Small / White', price: 45.00, inventoryQuantity: 200, sku: 'CCT-S-WHI' },
              { id: 'variant_5', title: 'Medium / White', price: 45.00, inventoryQuantity: 250, sku: 'CCT-M-WHI' }
            ]
          }
        ];
        
        setProducts(mockProducts);
      } catch (error) {
        console.error('Error fetching products:', error);
        toast.error('Failed to load products');
      }
    };
    
    fetchProducts();
  }, []);
  
  // Load existing campaign data if editing
  useEffect(() => {
    if (isEditing && campaignId) {
      const loadCampaign = async () => {
        try {
          setLoading(true);

          // Fetch campaign from API
          const response = await fetch(`/api/campaigns/${campaignId}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
            }
          });

          if (!response.ok) {
            throw new Error(`Failed to fetch campaign: ${response.status} ${response.statusText}`);
          }

          const data = await response.json();

          if (!data.success || !data.campaign) {
            throw new Error('Invalid campaign data received');
          }

          const campaign = data.campaign;
          console.log('✅ Loaded campaign for editing:', campaign);

          // Populate form with basic campaign data
          setValue('name', campaign.name || '');
          setValue('description', campaign.description || '');
          setValue('brandId', campaign.brandId?._id || campaign.brandId);

          // Set brand info
          if (campaign.brandId) {
            setSelectedBrand({
              id: campaign.brandId._id || campaign.brandId,
              name: campaign.brandId.name || 'Unknown Brand',
              slug: campaign.brandId.slug || ''
            });
          }

          // Populate content fields if they exist
          if (campaign.content) {
            Object.keys(campaign.content).forEach(key => {
              setValue(`content.${key}`, campaign.content[key]);
            });
          }

          // Populate production config if it exists
          if (campaign.production) {
            setProductionConfig({
              leadTimeDays: campaign.production.leadTimeDays || 21,
              shippingDays: campaign.production.shippingDays || 7,
              expediteDays: campaign.production.expediteDays || 0,
              inventoryBufferUnits: campaign.production.inventoryBufferUnits || 0
            });
          }

          // Handle multi-product campaigns
          if (campaign.products && campaign.products.length > 0) {
            console.log('📦 Multi-product campaign detected:', campaign.products.length, 'products');

            // Convert campaign products to selectedLooks format
            const looks = campaign.products.map(product => ({
              lookId: product.productId || product._id,
              id: product.sivadProduct?.productId || product.productId || product._id,
              handle: product.sivadProduct?.handle || product.handle || '',
              name: product.sivadProduct?.title || product.name || product.title || 'Unnamed Product',
              title: product.sivadProduct?.title || product.name || product.title || 'Unnamed Product',
              imageUrl: product.sivadProduct?.images?.[0] || product.imageUrl || product.image || '',
              estimatedCost: product.planning?.estimatedCost || product.estimatedCost || 0,
              units: product.planning?.units || product.units || 0,
              plannedDiscount: product.planning?.plannedDiscount || product.plannedDiscount || 0,
              categoryLabel: product.planning?.categoryLabel || product.categoryLabel || product.category || 'Product'
            }));

            setSelectedLooks(looks);

            // Restore product configurations
            const configs = {};
            campaign.products.forEach((product, index) => {
              if (product.pricingSnapshot) {
                configs[product.productId] = {
                  pricingData: product.pricingSnapshot,
                  tierProductionConfigs: {},
                  productionTemplate: product.productionTemplate ? { name: product.productionTemplate } : null
                };

                // Restore tier production configs
                if (product.tiers) {
                  product.tiers.forEach((tier, tierIndex) => {
                    if (tier.production) {
                      configs[product.productId].tierProductionConfigs[tierIndex + 1] = {
                        tierNumber: tierIndex + 1,
                        ...tier.production
                      };
                    }
                  });
                }
              }
            });

            setProductConfigurations(configs);
            console.log('✅ Restored product configurations:', Object.keys(configs).length, 'products');
          }
          // Handle legacy single-product campaigns
          else if (campaign.sivadProduct) {
            console.log('📦 Legacy single-product campaign detected');
            setSelectedProduct(campaign.sivadProduct);

            // If tiers exist, populate them
            if (campaign.tiers && campaign.tiers.length > 0) {
              // Convert to pricing data format if needed
              // This would require more work to reverse-engineer the pricing calculator format
              console.log('⚠️ Legacy tier data found but conversion not yet implemented');
            }
          }

        } catch (error) {
          console.error('❌ Error loading campaign:', error);
          toast.error(`Failed to load campaign: ${error.message}`);
        } finally {
          setLoading(false);
        }
      };

      loadCampaign();
    }
  }, [isEditing, campaignId, setValue]);
  
  // Get the current product being configured
  const getCurrentProduct = () => {
    if (selectedLooks.length === 0) return null;
    return selectedLooks[currentProductIndex];
  };

  const handleNext = () => {
    if (!isEditing && !selectedBrand) {
      toast.error('Select a brand to continue.');
      return;
    }

    // Step 1: Validate bimester and products selection
    if (activeStep === 1) {
      if (!selectedBimester) {
        toast.error('Please select a bimester first.');
        return;
      }
      if (selectedLooks.length === 0) {
        toast.error('Please select at least one product from the bimester.');
        return;
      }
      // Reset to first product when entering Step 3
      setCurrentProductIndex(0);
    }

    // Step 2 (Product Configuration): Check if we need to configure more products
    if (activeStep === 2) {
      const currentProduct = getCurrentProduct();

      // Save current product configuration
      if (currentProduct) {
        const currentConfig = {
          lookId: currentProduct.lookId,
          dropCycleId: currentProduct.dropCycle,
          productName: currentProduct.productInfo?.name || `${currentProduct.categoryLabel} #${currentProduct.sequence}`,
          pricingData: { ...pricingData },
          selectedTiers: Array.from(selectedTiers),
          availableTiers: [...availableTiers],
          calculatedPricing: calculatedPricing,
          campaignStartDate: campaignStartDate,
          sharedStartDateMode: sharedStartDateMode,
          tierProductionConfigs: { ...tierProductionConfigs }, // Save tier production configs
          productionTemplate: productionTemplate, // Save template reference
          configuredAt: new Date().toISOString()
        };

        setProductConfigurations(prev => ({
          ...prev,
          [currentProduct.lookId]: currentConfig
        }));
      }

      // If there are more products to configure, move to next product
      if (currentProductIndex < selectedLooks.length - 1) {
        // Load next product's saved configuration if it exists
        const nextProduct = selectedLooks[currentProductIndex + 1];
        const nextConfig = productConfigurations[nextProduct.lookId];

        if (nextConfig) {
          // Restore saved configuration
          setPricingData(nextConfig.pricingData || {});
          setSelectedTiers(new Set(nextConfig.selectedTiers || []));
          setAvailableTiers(nextConfig.availableTiers || []);
          setCalculatedPricing(nextConfig.calculatedPricing || null);
          setTierProductionConfigs(nextConfig.tierProductionConfigs || {});
          setProductionTemplate(nextConfig.productionTemplate || null);
          toast(`Loaded saved configuration for product ${currentProductIndex + 2}`, {
            icon: 'ℹ️',
          });
        } else {
          // Reset for new product configuration
          setSelectedTiers(new Set());
          setAvailableTiers([]);
          setCalculatedPricing(null);
          // tierProductionConfigs and productionTemplate will auto-populate via useEffect
        }

        setCurrentProductIndex(currentProductIndex + 1);
        toast.success(`Moving to product ${currentProductIndex + 2} of ${selectedLooks.length}`);
        return; // Stay on Step 3
      }
      // All products configured, move to next step
      toast.success('All products configured!');
    }

    if (activeStep < steps.length - 1) {
      setActiveStep(activeStep + 1);
    }
  };
  
  const handleBack = () => {
    // If on Step 3 (Configure Products) and not on first product, go to previous product
    if (activeStep === 2 && currentProductIndex > 0) {
      const currentProduct = getCurrentProduct();

      // Save current product configuration before going back
      if (currentProduct) {
        const currentConfig = {
          lookId: currentProduct.lookId,
          dropCycleId: currentProduct.dropCycle,
          productName: currentProduct.productInfo?.name || `${currentProduct.categoryLabel} #${currentProduct.sequence}`,
          pricingData: { ...pricingData },
          selectedTiers: Array.from(selectedTiers),
          availableTiers: [...availableTiers],
          calculatedPricing: calculatedPricing,
          campaignStartDate: campaignStartDate,
          sharedStartDateMode: sharedStartDateMode,
          tierProductionConfigs: { ...tierProductionConfigs },
          productionTemplate: productionTemplate,
          configuredAt: new Date().toISOString()
        };

        setProductConfigurations(prev => ({
          ...prev,
          [currentProduct.lookId]: currentConfig
        }));
      }

      // Load previous product's configuration
      const prevProduct = selectedLooks[currentProductIndex - 1];
      const prevConfig = productConfigurations[prevProduct.lookId];

      if (prevConfig) {
        setPricingData(prevConfig.pricingData || {});
        setSelectedTiers(new Set(prevConfig.selectedTiers || []));
        setAvailableTiers(prevConfig.availableTiers || []);
        setCalculatedPricing(prevConfig.calculatedPricing || null);
        setTierProductionConfigs(prevConfig.tierProductionConfigs || {});
        setProductionTemplate(prevConfig.productionTemplate || null);
      }

      setCurrentProductIndex(currentProductIndex - 1);
      toast(`Back to product ${currentProductIndex} of ${selectedLooks.length}`, {
        icon: 'ℹ️',
      });
      return;
    }

    // Otherwise, go to previous step
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };
  
  const onSubmit = async (data) => {
    if (!isEditing && !selectedBrand) {
      toast.error('Select a brand before saving.');
      return;
    }

    try {
      setLoading(true);

      // Build products array from configured products
      const products = selectedLooks.map((look, index) => {
        const config = productConfigurations[look.lookId] || {};

        return {
          productId: look.lookId,
          displayOrder: index,

          sivadProduct: {
            productId: look.id || look.lookId,
            handle: look.handle,
            title: look.name || look.title,
            images: look.imageUrl ? [look.imageUrl] : []
          },

          // Planning data
          planning: {
            estimatedCost: look.estimatedCost,
            units: look.units,
            plannedDiscount: look.plannedDiscount,
            categoryLabel: look.categoryLabel
          },

          // Configured pricing and tiers
          pricingSnapshot: config.pricingData,
          // FIXED: Use tiers from the look object (saved by createTiersFromResults)
          tiers: look.tiers?.map((tier, tierIndex) => {
            const tierNum = tierIndex + 1;
            const prodConfig = config.tierProductionConfigs?.[tierNum];

            return {
              ...tier,
              // Add production constraints to each tier
              production: prodConfig ? {
                state: prodConfig.state,
                method: prodConfig.method,
                threshold: prodConfig.threshold,
                leadTimeDays: prodConfig.leadTimeDays,
                costPerUnit: parseFloat(prodConfig.costPerUnit),
                setupCost: parseFloat(prodConfig.setupCost) || 0,
                supplier: prodConfig.supplier,
                notes: prodConfig.notes
              } : undefined
            };
          }) || [],

          // Production template used
          productionTemplate: config.productionTemplate?.name,

          // Status tracking
          status: config.pricingData?.tiers?.length > 0 ? 'configured' : 'incomplete'
        };
      });

      // Prepare campaign data with new timeline schema
      const campaignData = {
        ...data,
        brandId: data.brandId || selectedBrand?.id,
        brand: selectedBrand
          ? {
              id: selectedBrand.id,
              name: selectedBrand.name,
              slug: selectedBrand.slug,
            }
          : undefined,

        // Multi-product campaign data
        products: products,

        // Legacy fields for backward compatibility (use first product if available)
        production: productionConfig,
        sivadProduct: selectedLooks[0] ? {
          productId: selectedLooks[0].id,
          handle: selectedLooks[0].handle,
          title: selectedLooks[0].name || selectedLooks[0].title
        } : undefined,

        // REMOVED: Campaign-level tiers (tiers are now per-product)
        // Each product has its own tiers array
        tiers: []
      };

      console.log('Campaign data (new schema with products):', campaignData);

      // Save campaign to database
      const brandId = campaignData.brandId || selectedBrand?.id || selectedBrand?._id;
      const url = isEditing
        ? `/api/campaigns/${campaignId}`
        : `/api/campaigns/brand/${brandId}`;

      const response = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
        },
        body: JSON.stringify(campaignData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Campaign save failed:', errorData);
        throw new Error(errorData.error || 'Failed to save campaign');
      }

      const result = await response.json();
      console.log('✅ Campaign saved successfully:', result);

      if (isEditing) {
        toast.success('Campaign updated successfully');
      } else {
        toast.success('Campaign created successfully');
      }

      navigate('/campaigns');
    } catch (error) {
      console.error('Error saving campaign:', error);
      toast.error('Failed to save campaign');
    } finally {
      setLoading(false);
    }
  };
  
  const handleLaunchCampaign = async (data) => {
    if (!isEditing && !selectedBrand) {
      toast.error('Select a brand before launching.');
      return;
    }

    try {
      setLoading(true);
      
      // Save and launch campaign
      await onSubmit(data);
      
      // Additional launch logic would go here
      toast.success('Campaign launched successfully');
    } catch (error) {
      console.error('Error launching campaign:', error);
      toast.error('Failed to launch campaign');
    } finally {
      setLoading(false);
    }
  };
  

  // Calculate recommended price band category based on cost + margin/markup
  const getRecommendedPriceBandCategory = (cost, value, mode) => {
    if (!cost || !value) return null;

    let baseRetail;
    if (mode === 'margin') {
      const margin = value / 100;
      baseRetail = cost / (1 - margin);
    } else {
      // markup mode: multiplier (e.g., 2.5x)
      baseRetail = cost * value;
    }

    // Determine which price band category fits this retail price
    if (baseRetail < 50) return 'Budget/Streetwear ($20-100)';
    if (baseRetail < 120) return 'Mid-Range ($50-200)';
    if (baseRetail < 250) return 'Premium ($100-300)';
    if (baseRetail < 450) return 'Luxury ($200-500)';
    return 'High-End ($400+)';
  };

  // Calculate base retail for display
  const getBaseRetail = (cost, value, mode) => {
    if (!cost || !value) return 0;

    if (mode === 'margin') {
      const margin = value / 100;
      return cost / (1 - margin);
    } else {
      return cost * value;
    }
  };

  // Enhanced pricing calculation function
  // Handle price band preset selection with auto-suggestion of markup bands
  const handlePriceBandPresetChange = async (presetLabel) => {
    setSelectedPriceBandPreset(presetLabel);
    const preset = priceBandPresets.find(p => p.label === presetLabel);
    if (preset.value === 'custom') {
      setPricingData(prev => ({ ...prev, priceBands: customPriceBands || '' }));
    } else {
      setPricingData(prev => ({ ...prev, priceBands: preset.value }));

      // Auto-suggest markup bands based on price band category
      if (presetLabel !== 'Custom') {
        try {
          const encodedCategory = encodeURIComponent(presetLabel);
          const response = await fetch(`/api/campaigns/markup-bands/${encodedCategory}`);
          if (response.ok) {
            const result = await response.json();
            const suggestedMarkupBands = result.suggestedMarkupBands.join(',');
            setMarkupBands(suggestedMarkupBands);
            toast.success(`Auto-suggested markup bands for ${presetLabel}: ${suggestedMarkupBands}`);
          }
        } catch (error) {
          console.warn('Failed to fetch suggested markup bands:', error);
          // Don't show error to user, just keep current markup bands
        }
      }
    }
  };

  // Calculate markup bands overlay
  const calculateMarkupOverlay = async () => {
    if (!pricingData.cost || !pricingData.priceBands || !markupBands) {
      toast.error('Please fill in cost, price bands, and markup bands');
      return;
    }

    setCalculatingPricing(true);
    try {
      const bands = pricingData.priceBands.split(',').map(b => parseFloat(b.trim())).filter(b => !isNaN(b));
      const markupBandArray = markupBands.split(',').map(m => parseFloat(m.trim())).filter(m => !isNaN(m));

      const response = await fetch(`/api/campaigns/brand/${selectedProduct?.handle || 'sivad-ade'}/drop/calculate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          cost: parseFloat(pricingData.cost),
          bands: bands,
          markupBands: markupBandArray,
          lead_time_days: parseInt(pricingData.leadTimeDays) || 21,
          ship_days: parseInt(pricingData.shipDays) || 7,
          inventory_buffer_units: parseInt(pricingData.inventoryBufferUnits) || 0,
          expedite_days: parseInt(pricingData.expediteDays) || 0,
          minMur: parseFloat(pricingData.minMur) / 100 || 0.45
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to calculate markup overlay');
      }

      const result = await response.json();
      setMarkupOverlay(result.markupBandsOverlay);
      setShowMarkupOverlay(true);
      toast.success('Markup overlay calculated successfully!');
    } catch (error) {
      console.error('Error calculating markup overlay:', error);
      toast.error('Error calculating markup overlay: ' + error.message);
    } finally {
      setCalculatingPricing(false);
    }
  };

  // Handle tier selection toggle
  const handleTierSelection = (tierId, isSelected) => {
    const newSelection = new Set(selectedTiers);
    if (isSelected) {
      newSelection.add(tierId);
    } else {
      newSelection.delete(tierId);
    }
    setSelectedTiers(newSelection);
  };

  // Handle tier row expansion
  const handleTierExpansion = (tierId) => {
    const newExpanded = new Set(expandedTiers);
    if (expandedTiers.has(tierId)) {
      newExpanded.delete(tierId);
    } else {
      newExpanded.add(tierId);
    }
    setExpandedTiers(newExpanded);
  };

  // Handle tier configuration changes
  const handleTierConfigChange = (tierId, field, value) => {
    setTierConfigurations(prev => ({
      ...prev,
      [tierId]: {
        ...prev[tierId],
        [field]: value
      }
    }));
  };

  // Get tier configuration with defaults
  const getTierConfig = (tier) => {
    // Calculate reservation target based on total max and tier distribution
    const totalMaxReservation = parseInt(pricingData.maxReservationTarget) || 500;
    const numSelectedTiers = selectedTiers.size || 1;

    // Try to use expected tier distribution from pricing calculation
    let tierReservationTarget;
    if (calculatedPricing?.pricingSnapshotPreview?.inputs?.expectedTierDistribution) {
      const distribution = calculatedPricing.pricingSnapshotPreview.inputs.expectedTierDistribution;
      const tierIndex = tier.tier - 1; // tier.tier is 1-indexed
      const tierShare = distribution[tierIndex] || (1 / numSelectedTiers);
      tierReservationTarget = Math.floor(totalMaxReservation * tierShare);
    } else {
      // Fall back to equal distribution
      tierReservationTarget = Math.floor(totalMaxReservation / numSelectedTiers);
    }

    const defaultConfig = {
      name: tier.name,
      reservationTarget: tierReservationTarget,
      description: `${tier.name} tier with ${tier.waitDays === 0 ? 'immediate delivery after production' : `ships ${tier.waitDays} days after production completes`}`,
      productVariant: '',
      // Reservation window dates
      reservationStartDate: formatDateForInput(calculateTierStartDate(tier, campaignStartDate)),
      reservationEndDate: formatDateForInput(calculateTierEndDate(tier, campaignStartDate)),
      // Delivery priority
      priorityLevel: tier.waitDays || 0
    };

    return { ...defaultConfig, ...tierConfigurations[tier.tierId] };
  };

  // Automated tier date handling utilities
  const calculateTierStartDate = (tier, campaignStart) => {
    // In shared start date mode, all tiers start at the same time (campaign start date)
    // In independent mode, each tier starts based on its wait time
    if (sharedStartDateMode) {
      return new Date(campaignStart);
    }

    const customOffset = customWaitOffsets[tier.tierId];
    const waitDays = customOffset !== undefined ? customOffset : tier.waitDays;

    const startDate = new Date(campaignStart);
    startDate.setDate(startDate.getDate() + waitDays);
    return startDate;
  };

  const calculateTierEndDate = (tier, campaignStart) => {
    const windowLength = tier.waitWindowDays.endDay - tier.waitWindowDays.startDay;

    // In shared start date mode, end date is campaign start + window length
    // In independent mode, end date is start date + window length
    if (sharedStartDateMode) {
      const endDate = new Date(campaignStart);
      endDate.setDate(endDate.getDate() + windowLength);
      return endDate;
    }

    const customOffset = customWaitOffsets[tier.tierId];
    const waitDays = customOffset !== undefined ? customOffset : tier.waitDays;

    const endDate = new Date(campaignStart);
    endDate.setDate(endDate.getDate() + waitDays + windowLength);
    return endDate;
  };

  const handleWaitOffsetChange = (tierId, newOffset) => {
    setCustomWaitOffsets(prev => ({
      ...prev,
      [tierId]: parseInt(newOffset)
    }));
  };

  // Helper function to format date for datetime-local input
  const formatDateForInput = (date) => {
    const d = new Date(date);
    const pad = (num) => num.toString().padStart(2, '0');

    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  // Create tiers from selected pricing results with automated date handling
  const createTiersFromResults = () => {
    if (selectedTiers.size === 0) {
      toast.error('Please select at least one tier to create');
      return;
    }

    const currentProduct = getCurrentProduct();
    if (!currentProduct) {
      toast.error('No product selected. Please select a product first.');
      return;
    }

    const selectedTierData = availableTiers.filter(tier => selectedTiers.has(tier.tierId));
    const totalMaxReservation = parseInt(pricingData.maxReservationTarget) || 500;

    // Clear existing tiers and populate with selected ones using automated date calculation
    const newTiers = selectedTierData.map((tier, index) => {
      // In shared mode, use campaign start date. In independent mode, allow custom start dates
      const baseStartDate = sharedStartDateMode ? campaignStartDate : new Date(campaignStartDate.getTime() + (index * 7 * 24 * 60 * 60 * 1000)); // Stagger by weeks in independent mode
      const startDate = calculateTierStartDate(tier, baseStartDate);
      const endDate = calculateTierEndDate(tier, baseStartDate);

      // Calculate reservation target based on tier distribution
      let tierReservationTarget;
      if (calculatedPricing?.pricingSnapshotPreview?.inputs?.expectedTierDistribution) {
        const distribution = calculatedPricing.pricingSnapshotPreview.inputs.expectedTierDistribution;
        const tierIndex = tier.tier - 1; // tier.tier is 1-indexed
        const tierShare = distribution[tierIndex] || (1 / selectedTierData.length);
        tierReservationTarget = Math.floor(totalMaxReservation * tierShare);
      } else {
        // Fall back to equal distribution
        tierReservationTarget = Math.floor(totalMaxReservation / selectedTierData.length);
      }

      return {
        name: tier.name,
        description: `${tier.name} tier with ${tier.waitDays === 0 ? 'immediate delivery after production' : `ships ${tier.waitDays} days after production completes`}`,
        reservationTarget: tierReservationTarget, // Fixed: use reservationTarget to match schema
        maxReservationTarget: tierReservationTarget, // Keep for backward compatibility
        // New schema: reservation window
        reservationWindow: {
          startDate: formatDateForInput(startDate),
          endDate: formatDateForInput(endDate)
        },
        // New schema: delivery priority (inverse pricing model)
        delivery: {
          priorityLevel: tier.waitDays || 0,
          shippingWeeks: Math.ceil((productionConfig.leadTimeDays + productionConfig.shippingDays + tier.waitDays) / 7)
        },
        variantId: '',
        price: tier.price, // Use 'price' instead of 'calculatedPrice' to match schema
        calculatedPrice: tier.price, // Keep for backward compatibility
        margin: tier.margin,
        waitDays: tier.waitDays, // Keep for reference
        waitWindowDays: tier.waitWindowDays, // Keep for reference
        customWaitOffset: customWaitOffsets[tier.tierId] || tier.waitDays
      };
    });

    // FIXED: Save tiers to the CURRENT PRODUCT instead of campaign level
    const updatedLooks = [...selectedLooks];
    updatedLooks[currentProductIndex] = {
      ...currentProduct,
      tiers: newTiers
    };
    setSelectedLooks(updatedLooks);

    console.log(`✅ Created ${newTiers.length} tiers for product: ${currentProduct.name || currentProduct.categoryLabel}`, newTiers);
    toast.success(`Created ${selectedTiers.size} tiers for ${currentProduct.name || currentProduct.categoryLabel}`);
  };

  const calculatePricing = async () => {
    // Validate based on pricing mode
    if (!pricingData.cost || pricingData.cost === '') {
      toast.error('Please enter product cost');
      return;
    }

    const cost = parseFloat(pricingData.cost);
    if (isNaN(cost) || cost <= 0) {
      toast.error('Product cost must be a valid number greater than 0');
      return;
    }

    if (!pricingData.priceBands) {
      toast.error('Please fill in price bands');
      return;
    }

    if (pricingMode === 'margin' && !pricingData.targetMargin) {
      toast.error('Please enter target margin percentage');
      return;
    }

    setCalculatingPricing(true);
    try {
      const bands = pricingData.priceBands.split(',').map(b => parseFloat(b.trim())).filter(b => !isNaN(b));

      // Get planning data from the selected look (if available)
      const formData = watch();
      const planningData = formData._planningData || {};

      console.log('💰 Planning data available for pricing calculation:', {
        hasPlannedDiscount: !!planningData.plannedDiscount,
        plannedDiscount: planningData.plannedDiscount,
        hasExpectedTierDistribution: !!planningData.expectedTierDistribution,
        expectedTierDistribution: planningData.expectedTierDistribution,
        estimatedCost: planningData.estimatedCost
      });

      // Build request body based on pricing mode
      const requestBody = {
        cost: parseFloat(pricingData.cost),
        bands: bands,
        lead_time_days: parseInt(pricingData.leadTimeDays) || 21,
        ship_days: parseInt(pricingData.shipDays) || 7,
        maxReservationTarget: parseInt(pricingData.maxReservationTarget) || 500,
        inventory_buffer_units: parseInt(pricingData.inventoryBufferUnits) || 0,
        expedite_days: parseInt(pricingData.expediteDays) || 0,
        minMur: parseFloat(pricingData.minMur) / 100 || 0.45,
        // Flexible tier configuration
        tierCount: parseInt(pricingData.tierCount) || 4,
        tierNames: pricingData.tierNames,
        expectedTierDistribution: pricingData.expectedTierDistribution
      };

      // Add margin or markup based on mode
      if (pricingMode === 'margin') {
        requestBody.target_mur = parseFloat(pricingData.targetMargin) / 100;
      } else {
        // Multiplier mode: use default middle-range multiplier (will be overridden by auto-suggested markup bands)
        // The backend will generate tiers using the markup bands auto-suggested for the selected price band category
        requestBody.target_muc = 2.0; // Default 3x multiplier (2.0 markup)
      }

      // Smart planning data integration:
      // - Always respect user's tierCount and tierNames
      // - Use plannedDiscount from planning if available (for proportional calculation)
      // - Only use planning's expectedTierDistribution if tier counts match exactly

      const currentTierCount = requestBody.tierCount;
      const planningTierCount = planningData.expectedTierDistribution?.length;

      // Always use plannedDiscount if available (regardless of tier count)
      if (planningData.plannedDiscount != null) {
        requestBody.plannedDiscount = planningData.plannedDiscount;

        // Use planning's distribution ONLY if tier counts match
        if (planningData.expectedTierDistribution && planningTierCount === currentTierCount) {
          requestBody.expectedTierDistribution = planningData.expectedTierDistribution;
          console.log('✅ Using planning data (tier counts match):', {
            plannedDiscount: `${(planningData.plannedDiscount * 100).toFixed(0)}%`,
            tierCount: currentTierCount,
            source: 'planning distribution',
            expectedTierDistribution: planningData.expectedTierDistribution.map(d => `${(d * 100).toFixed(0)}%`).join(', ')
          });
        } else {
          // Use user's distribution with planning's discount
          console.log('✅ Using hybrid approach:', {
            plannedDiscount: `${(planningData.plannedDiscount * 100).toFixed(0)}% (from planning)`,
            tierCount: currentTierCount,
            source: 'user tier config',
            expectedTierDistribution: requestBody.expectedTierDistribution.map(d => `${(d * 100).toFixed(0)}%`).join(', ')
          });
          console.log('   → Backend will calculate proportional discounts for your tier config');
        }
      } else {
        console.log('ℹ️ Using manual tier configuration (no planning data):', {
          tierCount: requestBody.tierCount,
          tierNames: requestBody.tierNames,
          expectedTierDistribution: requestBody.expectedTierDistribution.map(d => `${(d * 100).toFixed(0)}%`).join(', ')
        });
      }

      console.log('📤 Sending pricing calculation request:', {
        tierCount: requestBody.tierCount,
        tierNamesCount: requestBody.tierNames?.length,
        tierNames: requestBody.tierNames,
        expectedTierDistributionCount: requestBody.expectedTierDistribution?.length,
        expectedTierDistribution: requestBody.expectedTierDistribution
      });

      // Get brandId from selected brand or form data
      const brandId = selectedBrand?.id || selectedBrand?._id || watch('brandId');

      if (!brandId) {
        toast.error('Please select a brand first');
        setCalculatingPricing(false);
        return;
      }

      const response = await fetch(`/api/campaigns/brand/${brandId}/drop/calculate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Pricing calculation failed:', errorData);
        throw new Error(errorData.error || 'Failed to calculate pricing');
      }

      const result = await response.json();
      console.log('📥 Pricing calculation response:', result);

      // Check if we got valid pricing data
      if (!result.calculation || !result.pricingSnapshotPreview?.tiers) {
        console.warn('⚠️ No tiers returned from pricing calculation:', result);
        toast('⚠️ Pricing calculated but no tiers generated. Check your inputs.', {
          icon: '⚠️',
          style: {
            background: '#ff9800',
            color: '#fff',
          },
        });
        setCalculatingPricing(false);
        return;
      }

      setCalculatedPricing(result);

      // Set available tiers and auto-select valid ones (meeting minMur and capacity requirements)
      if (result.pricingSnapshotPreview?.tiers) {
        const tiers = result.pricingSnapshotPreview.tiers;
        setAvailableTiers(tiers);

        // Auto-select valid tiers (no warnings and meeting requirements)
        const validTierIds = new Set();
        tiers.forEach(tier => {
          const hasWarnings = result.warnings && result.warnings.includes('BELOW_MIN_MUR') && tier.margin < 0.45;
          const capacityIssue = result.warnings && result.warnings.includes('CAPACITY_EXCEEDED');

          if (!hasWarnings && !capacityIssue) {
            validTierIds.add(tier.tierId);
          }
        });

        setSelectedTiers(validTierIds);

        // Tier data is now managed through the expandable breakdown table
        // No need to auto-populate old form fields
      }

      toast.success('Pricing calculated successfully!');
    } catch (error) {
      console.error('Error calculating pricing:', error);
      toast.error('Failed to calculate pricing');
    } finally {
      setCalculatingPricing(false);
    }
  };
  
  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Controller
                name="name"
                control={control}
                rules={{ required: 'Campaign name is required' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Campaign Name"
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    InputProps={{
                      sx: {
                        backgroundColor: 'rgba(255,255,255,0.04)',
                        borderRadius: 6,
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    multiline
                    rows={4}
                    label="Campaign Description"
                    placeholder="Describe your campaign..."
                    InputProps={{
                      sx: {
                        backgroundColor: 'rgba(255,255,255,0.04)',
                        borderRadius: 6,
                      },
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        );
        
      case 1:
        return (
          <BimesterProductSelector
            brandId={selectedBrand?.id || selectedBrand?._id}
            selectedLooks={selectedLooks}
            onUpdateSelection={(looks) => setSelectedLooks(looks)}
            selectedBimester={selectedBimester}
            onBimesterChange={(bimester) => setSelectedBimester(bimester)}
          />
        );
        
      case 2:
        const currentProduct = getCurrentProduct();

        return (
          <Box>
            {/* Current Product Indicator */}
            {selectedLooks.length > 0 && currentProduct && (
              <Card sx={{ mb: 3, bgcolor: 'primary.dark', color: 'white' }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="overline" sx={{ opacity: 0.8 }}>
                        Configuring Product {currentProductIndex + 1} of {selectedLooks.length}
                      </Typography>
                      <Typography variant="h5" fontWeight="bold">
                        {currentProduct.productInfo?.name || `${currentProduct.categoryLabel || 'Product'} ${currentProduct.sequence || ''}`}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
                        {currentProduct.categoryLabel || 'Product'} • {currentProduct.mode || 'Custom'} • From {currentProduct.dropCycleLabel || currentProduct.bimesterLabel || 'N/A'}
                      </Typography>
                    </Box>
                    <Box textAlign="right">
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        Planning Data
                      </Typography>
                      <Typography variant="body1">
                        Cost: <strong>${currentProduct.estimatedCost || 0}</strong>
                      </Typography>
                      <Typography variant="body2">
                        Units: {currentProduct.units || 0}
                        {currentProduct.plannedDiscount && ` • Discount: ${(currentProduct.plannedDiscount * 100).toFixed(0)}%`}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Timeline Model Explanation */}
            <Alert severity="info" sx={{ mb: 3 }}>
              <AlertTitle>Campaign Timeline Structure</AlertTitle>
              Your campaign timeline is divided into three clear phases:
              <Box component="ul" sx={{ mt: 1, mb: 0 }}>
                <li><strong>Reservation Period:</strong> When customers can place pre-orders (configured below)</li>
                <li><strong>Production Period:</strong> Manufacturing time after reservations close (Lead Time + Expedite options)</li>
                <li><strong>Delivery:</strong> Shipping to customers after production completes (inverse pricing: higher price = faster delivery)</li>
              </Box>
            </Alert>

            {/* Campaign Start Date Section */}
            <Card sx={{ mb: 3, p: 2 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Reservation Period Start</Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Campaign Start Date"
                    type="date"
                    value={campaignStartDate.toISOString().split('T')[0]}
                    onChange={(e) => setCampaignStartDate(new Date(e.target.value))}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    helperText="When the first tier opens for customer reservations"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Alert severity="info" sx={{ height: 'fit-content' }}>
                    Tier reservation windows will be auto-calculated from this date. Production starts when all reservations close.
                  </Alert>
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={sharedStartDateMode}
                        onChange={(e) => setSharedStartDateMode(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={sharedStartDateMode ? "Shared Start Date Mode" : "Independent Start Date Mode"}
                    sx={{ mb: 1 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {sharedStartDateMode
                      ? "All tiers use the campaign start date as their anchor point"
                      : "Each tier can have its own custom start date"
                    }
                  </Typography>
                </Grid>
              </Grid>
            </Card>

            {/* Pricing Calculator Section */}
            <Accordion sx={{ mb: 3 }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="pricing-calculator-content"
                id="pricing-calculator-header"
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalculateIcon />
                  <Typography variant="h6">Pricing Calculator</Typography>
                  {calculatedPricing && (
                    <Chip size="small" color="success" label="Calculated" />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Alert severity="info">
                      Calculate optimal pricing based on cost, margin, and price bands. This will auto-populate your tier pricing.
                    </Alert>
                  </Grid>

                  {/* Section 1: Pricing Method */}
                  <Grid item xs={12}>
                    <Box sx={{
                      p: 2.5,
                      bgcolor: 'rgba(255, 255, 255, 0.02)',
                      borderRadius: 6,
                      border: '1px solid rgba(245, 248, 255, 0.08)'
                    }}>
                      <Typography variant="subtitle1" sx={{ mb: 2.5, fontWeight: 600 }}>
                        Pricing Method
                      </Typography>
                      <Grid container spacing={2.5}>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            label="Product Cost ($)"
                            type="number"
                            value={pricingData.cost}
                            onChange={(e) => setPricingData(prev => ({ ...prev, cost: e.target.value }))}
                            placeholder="30.00"
                            helperText="Manufacturing/wholesale cost"
                            InputProps={{
                              sx: {
                                backgroundColor: 'rgba(255,255,255,0.04)',
                                borderRadius: 6,
                              },
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} md={4}>
                          <FormControl fullWidth>
                            <Box sx={{ mb: 1 }}>
                              <Typography variant="caption" color="text.secondary">
                                Calculation Method
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button
                                variant={pricingMode === 'margin' ? 'contained' : 'outlined'}
                                onClick={() => setPricingMode('margin')}
                                size="medium"
                                fullWidth
                                sx={{ height: '56px' }}
                              >
                                By Margin %
                              </Button>
                              <Button
                                variant={pricingMode === 'markup' ? 'contained' : 'outlined'}
                                onClick={() => setPricingMode('markup')}
                                size="medium"
                                fullWidth
                                sx={{ height: '56px' }}
                              >
                                By Multiplier
                              </Button>
                            </Box>
                          </FormControl>
                        </Grid>

                        {pricingMode === 'margin' && (
                          <Grid item xs={12} md={4}>
                            <TextField
                              fullWidth
                              label="Target Margin (%)"
                              type="number"
                              value={pricingData.targetMargin}
                              onChange={(e) => setPricingData(prev => ({ ...prev, targetMargin: e.target.value }))}
                              placeholder="62"
                              helperText="Desired profit margin (e.g., 62%)"
                              InputProps={{
                                sx: {
                                  backgroundColor: 'rgba(255,255,255,0.04)',
                                  borderRadius: 6,
                                },
                              }}
                            />
                          </Grid>
                        )}

                        {/* Note: When in Multiplier mode, no input field needed - auto-suggested multipliers from price band category will be used */}
                        {pricingMode === 'markup' && (
                          <Grid item xs={12} md={4}>
                            <Alert severity="info" sx={{ height: 'fit-content', display: 'flex', alignItems: 'center' }}>
                              Multipliers will be auto-suggested from selected price band category
                            </Alert>
                          </Grid>
                        )}
                      </Grid>
                    </Box>
                  </Grid>

                  {/* Smart Price Band Recommendation */}
                  {pricingData.cost && pricingMode === 'margin' && pricingData.targetMargin && (
                    <Grid item xs={12}>
                      <Alert
                        severity={
                          getRecommendedPriceBandCategory(
                            parseFloat(pricingData.cost),
                            parseFloat(pricingMode === 'margin' ? pricingData.targetMargin : markupMultiplier),
                            pricingMode
                          ) === selectedPriceBandPreset
                            ? 'success'
                            : 'warning'
                        }
                        sx={{
                          bgcolor: getRecommendedPriceBandCategory(
                            parseFloat(pricingData.cost),
                            parseFloat(pricingMode === 'margin' ? pricingData.targetMargin : markupMultiplier),
                            pricingMode
                          ) === selectedPriceBandPreset
                            ? 'success.dark'
                            : 'warning.dark'
                        }}
                      >
                        <AlertTitle>Price Band Recommendation</AlertTitle>
                        Based on your cost (${pricingData.cost}) and {pricingMode === 'margin' ? `target margin (${pricingData.targetMargin}%)` : `markup multiplier (${markupMultiplier}x)`},
                        your base retail price will be <strong>${getBaseRetail(
                          parseFloat(pricingData.cost),
                          parseFloat(pricingMode === 'margin' ? pricingData.targetMargin : markupMultiplier),
                          pricingMode
                        ).toFixed(2)}</strong>.
                        {' '}
                        <strong>Recommended category: {getRecommendedPriceBandCategory(
                          parseFloat(pricingData.cost),
                          parseFloat(pricingMode === 'margin' ? pricingData.targetMargin : markupMultiplier),
                          pricingMode
                        )}</strong>
                        {getRecommendedPriceBandCategory(
                          parseFloat(pricingData.cost),
                          parseFloat(pricingMode === 'margin' ? pricingData.targetMargin : markupMultiplier),
                          pricingMode
                        ) !== selectedPriceBandPreset && (
                          <Box sx={{ mt: 1 }}>
                            You've selected <strong>{selectedPriceBandPreset}</strong>, which may not align well with your target pricing.
                          </Box>
                        )}
                      </Alert>
                    </Grid>
                  )}

                  {/* Section 2: Price Band Selection */}
                  <Grid item xs={12}>
                    <Box sx={{
                      p: 2.5,
                      bgcolor: 'rgba(255, 255, 255, 0.02)',
                      borderRadius: 6,
                      border: '1px solid rgba(245, 248, 255, 0.08)'
                    }}>
                      <Typography variant="subtitle1" sx={{ mb: 2.5, fontWeight: 600 }}>
                        Price Band Configuration
                      </Typography>
                      <Grid container spacing={2.5}>
                        <Grid item xs={12} md={6}>
                          <FormControl
                            fullWidth
                            variant="outlined"
                            sx={{
                              '& .MuiInputLabel-root': {
                                color: 'rgba(245, 248, 255, 0.75)',
                              },
                              '& .MuiInputLabel-root.Mui-focused': {
                                color: '#08D2FF',
                              },
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: 'rgba(255, 255, 255, 0.04)',
                                borderRadius: 6,
                                '& .MuiSelect-select': {
                                  color: '#F5F8FF',
                                },
                                '& fieldset': {
                                  borderColor: 'rgba(245, 248, 255, 0.14)',
                                },
                                '&:hover fieldset': {
                                  borderColor: 'rgba(8, 210, 255, 0.65)',
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: '#08D2FF',
                                  boxShadow: '0 0 0 3px rgba(8, 210, 255, 0.18)',
                                },
                              },
                            }}
                          >
                            <InputLabel id="price-band-label">Price Band Category</InputLabel>
                            <Select
                              labelId="price-band-label"
                              value={selectedPriceBandPreset}
                              onChange={(e) => handlePriceBandPresetChange(e.target.value)}
                              label="Price Band Category"
                              MenuProps={{
                                PaperProps: {
                                  sx: {
                                    bgcolor: '#141B2B',
                                    color: '#F5F8FF',
                                    border: '1px solid rgba(245, 248, 255, 0.12)',
                                  },
                                },
                              }}
                            >
                              {priceBandPresets.map((preset) => (
                                <MenuItem key={preset.label} value={preset.label}>
                                  {preset.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>

                        {selectedPriceBandPreset === 'Custom' ? (
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              label="Custom Price Bands"
                              value={customPriceBands}
                              onChange={(e) => {
                                setCustomPriceBands(e.target.value);
                                setPricingData(prev => ({ ...prev, priceBands: e.target.value }));
                              }}
                              placeholder="49,59,69,79,89,99"
                              helperText="Comma-separated price points"
                              InputProps={{
                                sx: {
                                  backgroundColor: 'rgba(255,255,255,0.04)',
                                  borderRadius: 6,
                                },
                              }}
                            />
                          </Grid>
                        ) : (
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              label="Selected Price Bands"
                              value={pricingData.priceBands}
                              helperText="Auto-selected from category"
                              InputProps={{
                                readOnly: true,
                                sx: {
                                  backgroundColor: 'rgba(255,255,255,0.04)',
                                  borderRadius: 6,
                                  color: '#F5F8FF',
                                },
                              }}
                            />
                          </Grid>
                        )}
                      </Grid>
                    </Box>
                  </Grid>

                  {/* Section 3: Tier Configuration */}
                  <Grid item xs={12}>
                    <Box sx={{
                      p: 2.5,
                      bgcolor: 'rgba(255, 255, 255, 0.02)',
                      borderRadius: 6,
                      border: '1px solid rgba(245, 248, 255, 0.08)'
                    }}>
                      <Typography variant="subtitle1" sx={{ mb: 2.5, fontWeight: 600 }}>
                        Tier Configuration
                      </Typography>
                      <Grid container spacing={2.5}>
                        {/* Tier Count Selector */}
                        <Grid item xs={12} md={4}>
                          <FormControl fullWidth>
                            <InputLabel>Number of Tiers</InputLabel>
                            <Select
                              value={pricingData.tierCount}
                              onChange={(e) => {
                                const newTierCount = parseInt(e.target.value);

                                console.log('🔄 Tier count changing:', {
                                  from: pricingData.tierCount,
                                  to: newTierCount
                                });

                                // Generate default tier names and distribution for new tier count
                                const defaultTierNames = {
                                  2: ['Express', 'Pre-Order'],
                                  3: ['Express', 'Standard', 'Pre-Order'],
                                  4: ['Express', 'Standard', 'Early Bird', 'Pre-Order'],
                                  5: ['Express', 'Fast Track', 'Standard', 'Early Bird', 'Pre-Order'],
                                  6: ['Express', 'Fast Track', 'Standard', 'Early Bird', 'Advance Order', 'Pre-Order']
                                };

                                const defaultDistributions = {
                                  2: [0.30, 0.70],
                                  3: [0.15, 0.35, 0.50],
                                  4: [0.075, 0.20, 0.35, 0.375],
                                  5: [0.075, 0.125, 0.25, 0.30, 0.25],
                                  6: [0.05, 0.10, 0.20, 0.25, 0.25, 0.15]
                                };

                                const newState = {
                                  tierCount: newTierCount,
                                  tierNames: defaultTierNames[newTierCount] || defaultTierNames[4],
                                  expectedTierDistribution: defaultDistributions[newTierCount] || defaultDistributions[4]
                                };

                                console.log('📝 New tier configuration:', newState);

                                setPricingData(prev => {
                                  console.log('📋 Previous state:', {
                                    tierCount: prev.tierCount,
                                    tierNames: prev.tierNames,
                                    expectedTierDistribution: prev.expectedTierDistribution
                                  });
                                  return {
                                    ...prev,
                                    ...newState
                                  };
                                });
                              }}
                              sx={{
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: 'rgba(245, 248, 255, 0.2)',
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                  borderColor: 'rgba(245, 248, 255, 0.4)',
                                },
                              }}
                            >
                              <MenuItem value={2}>2 Tiers</MenuItem>
                              <MenuItem value={3}>3 Tiers</MenuItem>
                              <MenuItem value={4}>4 Tiers (Default)</MenuItem>
                              <MenuItem value={5}>5 Tiers</MenuItem>
                              <MenuItem value={6}>6 Tiers</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>

                        {/* Distribution Summary */}
                        <Grid item xs={12} md={8}>
                          <Alert severity="info" sx={{ bgcolor: 'rgba(33, 150, 243, 0.15)' }}>
                            <strong>{pricingData.tierCount} tiers</strong> configured with distribution: {pricingData.expectedTierDistribution.map((d, i) => `${pricingData.tierNames[i]} ${(d * 100).toFixed(1)}%`).join(', ')}
                          </Alert>
                        </Grid>

                        {/* Dynamic Tier Name Inputs */}
                        {pricingData.tierNames.map((name, index) => (
                          <Grid item xs={12} md={6} lg={4} key={`tier-name-${index}`}>
                            <TextField
                              fullWidth
                              label={`Tier ${index + 1} Name`}
                              value={name}
                              onChange={(e) => {
                                const newTierNames = [...pricingData.tierNames];
                                newTierNames[index] = e.target.value;
                                setPricingData(prev => ({ ...prev, tierNames: newTierNames }));
                              }}
                              InputProps={{
                                sx: {
                                  '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'rgba(245, 248, 255, 0.2)',
                                  },
                                  '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'rgba(245, 248, 255, 0.4)',
                                  },
                                },
                              }}
                            />
                          </Grid>
                        ))}

                        {/* Dynamic Distribution Inputs */}
                        <Grid item xs={12}>
                          <Divider sx={{ my: 1, borderColor: 'rgba(245, 248, 255, 0.08)' }} />
                          <Typography variant="subtitle2" sx={{ mb: 2, mt: 2, fontWeight: 600 }}>
                            Expected Sales Distribution
                            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                              Percentage of total sales expected per tier (must sum to 100%)
                            </Typography>
                          </Typography>
                        </Grid>

                        {pricingData.expectedTierDistribution.map((dist, index) => (
                          <Grid item xs={12} md={6} lg={4} key={`tier-dist-${index}`}>
                            <TextField
                              fullWidth
                              label={`${pricingData.tierNames[index]} Distribution (%)`}
                              type="number"
                              value={(dist * 100).toFixed(1)}
                              onChange={(e) => {
                                const newDist = [...pricingData.expectedTierDistribution];
                                newDist[index] = parseFloat(e.target.value) / 100;
                                setPricingData(prev => ({ ...prev, expectedTierDistribution: newDist }));
                              }}
                              inputProps={{ step: 0.1, min: 0, max: 100 }}
                              InputProps={{
                                sx: {
                                  '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'rgba(245, 248, 255, 0.2)',
                                  },
                                  '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'rgba(245, 248, 255, 0.4)',
                                  },
                                },
                              }}
                            />
                          </Grid>
                        ))}

                        {/* Distribution Validation */}
                        <Grid item xs={12}>
                          {(() => {
                            const sum = pricingData.expectedTierDistribution.reduce((a, b) => a + b, 0);
                            const isValid = Math.abs(sum - 1.0) < 0.01;
                            return (
                              <Alert
                                severity={isValid ? 'success' : 'warning'}
                                sx={{
                                  bgcolor: isValid ? 'rgba(76, 175, 80, 0.15)' : 'rgba(255, 152, 0, 0.15)'
                                }}
                              >
                                {isValid
                                  ? `✓ Distribution is valid (${(sum * 100).toFixed(1)}%)`
                                  : `⚠️ Distribution must sum to 100% (currently ${(sum * 100).toFixed(1)}%)`
                                }
                              </Alert>
                            );
                          })()}
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>

                  {/* Section 4: Production & Constraints */}
                  <Grid item xs={12}>
                    <Box sx={{
                      p: 2.5,
                      bgcolor: 'rgba(255, 255, 255, 0.02)',
                      borderRadius: 6,
                      border: '1px solid rgba(245, 248, 255, 0.08)'
                    }}>
                      <Typography variant="subtitle1" sx={{ mb: 2.5, fontWeight: 600 }}>
                        Production & Constraints
                      </Typography>
                      <Grid container spacing={2.5}>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Production Lead Time (days)"
                            type="number"
                            value={pricingData.leadTimeDays}
                            onChange={(e) => setPricingData(prev => ({ ...prev, leadTimeDays: e.target.value }))}
                            placeholder="21"
                            helperText="Manufacturing time after reservations close"
                            InputProps={{
                              sx: {
                                backgroundColor: 'rgba(255,255,255,0.04)',
                                borderRadius: 6,
                              },
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Shipping Days"
                            type="number"
                            value={pricingData.shipDays}
                            onChange={(e) => setPricingData(prev => ({ ...prev, shipDays: e.target.value }))}
                            placeholder="7"
                            helperText="Transit time after production completes"
                            InputProps={{
                              sx: {
                                backgroundColor: 'rgba(255,255,255,0.04)',
                                borderRadius: 6,
                              },
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Max Reservation Target"
                            type="number"
                            value={pricingData.maxReservationTarget}
                            onChange={(e) => setPricingData(prev => ({ ...prev, maxReservationTarget: e.target.value }))}
                            placeholder="500"
                            helperText="Maximum units across all tiers"
                            InputProps={{
                              sx: {
                                backgroundColor: 'rgba(255,255,255,0.04)',
                                borderRadius: 6,
                              },
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Min Margin Floor (%)"
                            type="number"
                            value={pricingData.minMur}
                            onChange={(e) => setPricingData(prev => ({ ...prev, minMur: e.target.value }))}
                            placeholder="45"
                            helperText="Minimum acceptable profit margin"
                            InputProps={{
                              sx: {
                                backgroundColor: 'rgba(255,255,255,0.04)',
                                borderRadius: 6,
                              },
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>

                  {/* Section 4: Advanced Tools (Collapsible) */}
                  <Grid item xs={12}>
                    <Accordion sx={{ bgcolor: 'rgba(255, 255, 255, 0.02)', border: '1px solid rgba(245, 248, 255, 0.08)' }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Advanced Comparison Tools
                          </Typography>
                          <Chip size="small" label="Optional" variant="outlined" />
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box sx={{ p: 1 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Compare theoretical (markup-driven) prices vs. snapped (brand-aligned) prices
                          </Typography>

                          <Grid container spacing={2.5} alignItems="flex-start">
                            <Grid item xs={12} md={8}>
                              <TextField
                                fullWidth
                                label="Markup Bands"
                                value={markupBands}
                                onChange={(e) => setMarkupBands(e.target.value)}
                                placeholder="1.4,1.6,1.8,2.0"
                                helperText="Auto-suggested based on price band category. You may override. (e.g., 1.4 = 40% markup)"
                                InputProps={{
                                  sx: {
                                    backgroundColor: 'rgba(255,255,255,0.04)',
                                    borderRadius: 6,
                                  },
                                }}
                              />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <Button
                                variant="outlined"
                                startIcon={calculatingPricing ? <CircularProgress size={20} /> : <CalculateIcon />}
                                onClick={calculateMarkupOverlay}
                                disabled={calculatingPricing || !pricingData.cost || !markupBands}
                                fullWidth
                                sx={{ height: '56px' }}
                              >
                                {calculatingPricing ? 'Calculating...' : 'Calculate Overlay'}
                              </Button>
                            </Grid>
                          </Grid>
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  </Grid>

                  {/* Calculate Pricing Button */}
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
                      <Button
                        variant="contained"
                        size="large"
                        startIcon={calculatingPricing ? <CircularProgress size={20} /> : <CalculateIcon />}
                        onClick={calculatePricing}
                        disabled={calculatingPricing || !pricingData.cost || (pricingMode === 'margin' && !pricingData.targetMargin)}
                        sx={{
                          minWidth: 250,
                          py: 1.5,
                          fontSize: '1rem',
                          fontWeight: 600
                        }}
                      >
                        {calculatingPricing ? 'Calculating Pricing...' : 'Calculate Tier Pricing'}
                      </Button>
                    </Box>
                  </Grid>

                  {calculatedPricing && (
                    <Grid item xs={12}>
                      {/* Warnings Section */}
                      {calculatedPricing.warnings && calculatedPricing.warnings.length > 0 && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                          <AlertTitle>Pricing Warnings</AlertTitle>
                          <Box component="ul" sx={{ m: 0, pl: 2 }}>
                            {calculatedPricing.warnings.map((warning, index) => (
                              <li key={index}>
                                {warning === 'BELOW_MIN_MUR' && 'Some tiers fall below minimum margin floor'}
                                {warning === 'CAPACITY_EXCEEDED' && 'Max reservation target exceeds estimated capacity'}
                              </li>
                            ))}
                          </Box>
                        </Alert>
                      )}

                      {/* Enhanced Pricing Results */}
                      <Card variant="outlined" sx={{ mt: 2 }}>
                        <CardContent>
                          <Typography variant="h6" sx={{ mb: 2 }}>Enhanced Pricing Results</Typography>

                          {/* Pricing Snapshot Preview */}
                          {calculatedPricing.pricingSnapshotPreview && (
                            <Card variant="outlined" sx={{ mb: 3, bgcolor: 'background.default' }}>
                              <CardContent>
                                <Typography variant="subtitle1" sx={{ mb: 2 }}>Pricing Snapshot</Typography>
                                <Grid container spacing={2}>
                                  <Grid item xs={6} md={3}>
                                    <Typography variant="body2" color="text.secondary">Base Retail</Typography>
                                    <Typography variant="h6">${calculatedPricing.pricingSnapshotPreview.baseRetail}</Typography>
                                  </Grid>
                                  <Grid item xs={6} md={3}>
                                    <Typography variant="body2" color="text.secondary">Total Days</Typography>
                                    <Typography variant="h6">{calculatedPricing.pricingSnapshotPreview.totalDays}</Typography>
                                  </Grid>
                                  <Grid item xs={6} md={3}>
                                    <Typography variant="body2" color="text.secondary">Earliest Wait</Typography>
                                    <Typography variant="h6">{calculatedPricing.pricingSnapshotPreview.earliestWaitDays} days</Typography>
                                  </Grid>
                                  <Grid item xs={6} md={3}>
                                    <Typography variant="body2" color="text.secondary">Snapshot ID</Typography>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                      {calculatedPricing.pricingSnapshotPreview.id.slice(-8)}
                                    </Typography>
                                  </Grid>
                                </Grid>

                                {/* Production & Constraints Inputs */}
                                {calculatedPricing.pricingSnapshotPreview?.inputs && (
                                  <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid rgba(245, 248, 255, 0.08)' }}>
                                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                                      Production & Constraints Used
                                    </Typography>
                                    <Grid container spacing={2}>
                                      <Grid item xs={6} md={4}>
                                        <Typography variant="body2" color="text.secondary">Lead Time</Typography>
                                        <Typography variant="body1">{calculatedPricing.pricingSnapshotPreview.inputs.lead_time_days} days</Typography>
                                      </Grid>
                                      <Grid item xs={6} md={4}>
                                        <Typography variant="body2" color="text.secondary">Shipping</Typography>
                                        <Typography variant="body1">{calculatedPricing.pricingSnapshotPreview.inputs.ship_days} days</Typography>
                                      </Grid>
                                      <Grid item xs={6} md={4}>
                                        <Typography variant="body2" color="text.secondary">Min Margin</Typography>
                                        <Typography variant="body1">{(calculatedPricing.pricingSnapshotPreview.inputs.minMur * 100).toFixed(0)}%</Typography>
                                      </Grid>
                                      <Grid item xs={6} md={4}>
                                        <Typography variant="body2" color="text.secondary">Inventory Buffer</Typography>
                                        <Typography variant="body1">{calculatedPricing.pricingSnapshotPreview.inputs.inventory_buffer_units} units</Typography>
                                      </Grid>
                                      <Grid item xs={6} md={4}>
                                        <Typography variant="body2" color="text.secondary">Expedite Days</Typography>
                                        <Typography variant="body1">{calculatedPricing.pricingSnapshotPreview.inputs.expedite_days} days</Typography>
                                      </Grid>
                                      <Grid item xs={6} md={4}>
                                        <Typography variant="body2" color="text.secondary">Max Reservation</Typography>
                                        <Typography variant="body1">{calculatedPricing.pricingSnapshotPreview.inputs.maxReservationTarget} units</Typography>
                                      </Grid>
                                      {calculatedPricing.pricingSnapshotPreview.inputs.usePriceBands !== undefined && (
                                        <Grid item xs={12} md={4}>
                                          <Typography variant="body2" color="text.secondary">Price Bands</Typography>
                                          <Typography variant="body1">{calculatedPricing.pricingSnapshotPreview.inputs.usePriceBands ? 'Enabled' : 'Disabled'}</Typography>
                                        </Grid>
                                      )}
                                    </Grid>
                                  </Box>
                                )}

                                {/* Tier Configuration Used */}
                                {calculatedPricing.pricingSnapshotPreview?.inputs?.tierCount && (
                                  <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid rgba(245, 248, 255, 0.08)' }}>
                                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                                      Tier Configuration Used
                                    </Typography>
                                    <Grid container spacing={2}>
                                      <Grid item xs={6} md={4}>
                                        <Typography variant="body2" color="text.secondary">Number of Tiers</Typography>
                                        <Typography variant="body1">{calculatedPricing.pricingSnapshotPreview.inputs.tierCount}</Typography>
                                      </Grid>
                                      <Grid item xs={12} md={8}>
                                        <Typography variant="body2" color="text.secondary">Tier Names</Typography>
                                        <Typography variant="body1">
                                          {calculatedPricing.pricingSnapshotPreview.inputs.tierNames?.join(' → ') || 'Default'}
                                        </Typography>
                                      </Grid>
                                      <Grid item xs={12}>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Expected Distribution</Typography>
                                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                          {calculatedPricing.pricingSnapshotPreview.inputs.expectedTierDistribution?.map((dist, i) => (
                                            <Chip
                                              key={i}
                                              label={`${calculatedPricing.pricingSnapshotPreview.inputs.tierNames?.[i] || `Tier ${i+1}`}: ${(dist * 100).toFixed(1)}%`}
                                              size="small"
                                              color={i === 0 ? 'error' : i === 1 ? 'warning' : i === 2 ? 'success' : 'info'}
                                              variant="outlined"
                                            />
                                          ))}
                                        </Box>
                                      </Grid>
                                      {calculatedPricing.pricingSnapshotPreview.inputs.plannedDiscount !== null && calculatedPricing.pricingSnapshotPreview.inputs.plannedDiscount !== undefined && (
                                        <Grid item xs={6} md={4}>
                                          <Typography variant="body2" color="text.secondary">Planned Discount</Typography>
                                          <Typography variant="body1">{(calculatedPricing.pricingSnapshotPreview.inputs.plannedDiscount * 100).toFixed(0)}%</Typography>
                                        </Grid>
                                      )}
                                    </Grid>
                                  </Box>
                                )}
                              </CardContent>
                            </Card>
                          )}

                          {/* Enhanced Tier Table with Expandable Rows */}
                          {calculatedPricing.pricingSnapshotPreview?.tiers && (
                            <Box>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="subtitle1">Tier Breakdown</Typography>
                                <Button
                                  variant="contained"
                                  color="primary"
                                  onClick={createTiersFromResults}
                                  disabled={selectedTiers.size === 0}
                                  size="small"
                                >
                                  Create {selectedTiers.size} Selected Tiers
                                </Button>
                              </Box>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell padding="checkbox">Select</TableCell>
                                    <TableCell></TableCell>
                                    <TableCell>Tier</TableCell>
                                    <TableCell align="right">Price</TableCell>
                                    <TableCell align="right">Margin %</TableCell>
                                    <TableCell align="center">Wait Days</TableCell>
                                    <TableCell align="center">Start Date</TableCell>
                                    <TableCell align="center">End Date</TableCell>
                                    <TableCell align="right">Discount</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {calculatedPricing.pricingSnapshotPreview.tiers.map((tier, index) => {
                                    const isSelected = selectedTiers.has(tier.tierId);
                                    const isExpanded = expandedTiers.has(tier.tierId);
                                    const hasWarnings = calculatedPricing.warnings && calculatedPricing.warnings.includes('BELOW_MIN_MUR') && tier.margin < 0.45;
                                    const tierConfig = getTierConfig(tier);

                                    return (
                                      <React.Fragment key={tier.tierId || index}>
                                        {/* Main Row */}
                                        <TableRow hover onClick={() => handleTierExpansion(tier.tierId)} sx={{ cursor: 'pointer' }}>
                                          <TableCell padding="checkbox">
                                            <Checkbox
                                              checked={isSelected}
                                              onChange={(e) => {
                                                e.stopPropagation();
                                                handleTierSelection(tier.tierId, e.target.checked);
                                              }}
                                              disabled={hasWarnings}
                                              color="primary"
                                            />
                                          </TableCell>
                                          <TableCell>
                                            <IconButton
                                              aria-label="expand row"
                                              size="small"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleTierExpansion(tier.tierId);
                                              }}
                                            >
                                              {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                            </IconButton>
                                          </TableCell>
                                          <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                              <Typography variant="body2" fontWeight="medium">{tier.name}</Typography>
                                              {tier.label === 'Ships now' && <Chip size="small" label="Ships Now" color="success" />}
                                              {hasWarnings && <Chip size="small" label="Below Min Margin" color="error" />}
                                            </Box>
                                          </TableCell>
                                          <TableCell align="right">
                                            <Typography variant="body1" fontWeight="medium">${tier.price}</Typography>
                                          </TableCell>
                                          <TableCell align="right">
                                            <Typography
                                              variant="body2"
                                              color={tier.margin < 0.45 ? 'error' : 'text.primary'}
                                            >
                                              {(tier.margin * 100).toFixed(1)}%
                                            </Typography>
                                          </TableCell>
                                          <TableCell align="center">
                                            <TextField
                                              size="small"
                                              type="number"
                                              value={customWaitOffsets[tier.tierId] || tier.waitDays}
                                              onChange={(e) => {
                                                e.stopPropagation();
                                                handleWaitOffsetChange(tier.tierId, e.target.value);
                                              }}
                                              inputProps={{ min: 0, style: { textAlign: 'center', width: '60px' } }}
                                              variant="outlined"
                                            />
                                          </TableCell>
                                          <TableCell align="center">
                                            <Typography variant="body2" color="text.secondary">
                                              {calculateTierStartDate(tier, campaignStartDate).toLocaleDateString()}
                                            </Typography>
                                          </TableCell>
                                          <TableCell align="center">
                                            <Typography variant="body2" color="text.secondary">
                                              {calculateTierEndDate(tier, campaignStartDate).toLocaleDateString()}
                                            </Typography>
                                          </TableCell>
                                          <TableCell align="right">
                                            <Typography variant="body2" color="text.secondary">
                                              {(tier.discount * 100).toFixed(0)}%
                                            </Typography>
                                          </TableCell>
                                        </TableRow>

                                        {/* Expanded Detail Row */}
                                        {isExpanded && (
                                          <TableRow>
                                            <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
                                              <Box sx={{ margin: 1 }}>
                                                <Card variant="outlined" sx={{ bgcolor: 'background.default' }}>
                                                  <CardContent>
                                                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
                                                      Configure {tier.name} Tier
                                                    </Typography>

                                                    <Grid container spacing={2}>
                                                      {/* Tier Name */}
                                                      <Grid item xs={12} md={6}>
                                                        <TextField
                                                          fullWidth
                                                          label="Tier Name"
                                                          value={tierConfig.name}
                                                          onChange={(e) => handleTierConfigChange(tier.tierId, 'name', e.target.value)}
                                                          size="small"
                                                        />
                                                      </Grid>

                                                      {/* Reservation Target */}
                                                      <Grid item xs={12} md={6}>
                                                        <TextField
                                                          fullWidth
                                                          label="Reservation Target"
                                                          type="number"
                                                          value={tierConfig.reservationTarget}
                                                          onChange={(e) => handleTierConfigChange(tier.tierId, 'reservationTarget', parseInt(e.target.value))}
                                                          size="small"
                                                          helperText={`Max: ${pricingData.maxReservationTarget}`}
                                                        />
                                                      </Grid>

                                                      {/* Calculated Price (Read-only) */}
                                                      <Grid item xs={12} md={6}>
                                                        <TextField
                                                          fullWidth
                                                          label="Calculated Price ($)"
                                                          value={tier.price}
                                                          InputProps={{ readOnly: true }}
                                                          size="small"
                                                          sx={{
                                                            '& .MuiInputBase-input': {
                                                              backgroundColor: '#171b26',
                                                            },
                                                          }}
                                                        />
                                                      </Grid>

                                                      {/* Product Variant */}
                                                      <Grid item xs={12} md={6}>
                                                        <FormControl fullWidth size="small">
                                                          <InputLabel>Product Variant</InputLabel>
                                                          <Select
                                                            value={tierConfig.productVariant}
                                                            onChange={(e) => handleTierConfigChange(tier.tierId, 'productVariant', e.target.value)}
                                                            label="Product Variant"
                                                            disabled={!selectedProduct}
                                                          >
                                                            {selectedProduct?.variants?.map((variant) => (
                                                              <MenuItem key={variant.id} value={variant.id}>
                                                                {variant.title} - ${variant.price} (Stock: {variant.inventoryQuantity})
                                                              </MenuItem>
                                                            ))}
                                                          </Select>
                                                        </FormControl>
                                                      </Grid>

                                                      {/* Tier Description */}
                                                      <Grid item xs={12}>
                                                        <TextField
                                                          fullWidth
                                                          label="Tier Description"
                                                          multiline
                                                          rows={3}
                                                          value={tierConfig.description}
                                                          onChange={(e) => handleTierConfigChange(tier.tierId, 'description', e.target.value)}
                                                          size="small"
                                                          placeholder="Describe this tier for customers..."
                                                        />
                                                      </Grid>

                                                      {/* Start Date & Time */}
                                                      <Grid item xs={12} md={6}>
                                                        <TextField
                                                          fullWidth
                                                          label="Start Date & Time"
                                                          type="datetime-local"
                                                          value={tierConfig.startDateTime}
                                                          onChange={(e) => handleTierConfigChange(tier.tierId, 'startDateTime', e.target.value)}
                                                          InputLabelProps={{ shrink: true }}
                                                          size="small"
                                                          disabled={sharedStartDateMode}
                                                          helperText={sharedStartDateMode ? "Auto-calculated from campaign start" : "Custom start date"}
                                                        />
                                                      </Grid>

                                                      {/* End Date & Time (Read-only) */}
                                                      <Grid item xs={12} md={6}>
                                                        <TextField
                                                          fullWidth
                                                          label="End Date & Time"
                                                          type="datetime-local"
                                                          value={tierConfig.endDateTime}
                                                          InputLabelProps={{ shrink: true }}
                                                          InputProps={{ readOnly: true }}
                                                          size="small"
                                                          helperText="Auto-calculated from start date + wait window"
                                                          sx={{
                                                            '& .MuiInputBase-input': {
                                                              backgroundColor: '#171b26',
                                                            },
                                                          }}
                                                        />
                                                      </Grid>
                                                    </Grid>

                                                    {/* Validation Warnings */}
                                                    {tierConfig.reservationTarget > parseInt(pricingData.maxReservationTarget) && (
                                                      <Alert severity="warning" sx={{ mt: 2 }}>
                                                        Reservation target exceeds campaign maximum ({pricingData.maxReservationTarget})
                                                      </Alert>
                                                    )}

                                                    {!tierConfig.productVariant && (
                                                      <Alert severity="info" sx={{ mt: 2 }}>
                                                        Product variant must be selected before campaign launch
                                                      </Alert>
                                                    )}

                                                    {hasWarnings && (
                                                      <Alert severity="error" sx={{ mt: 2 }}>
                                                        This tier falls below minimum margin floor and cannot be included in campaign
                                                      </Alert>
                                                    )}
                                                  </CardContent>
                                                </Card>
                                              </Box>
                                            </TableCell>
                                          </TableRow>
                                        )}
                                      </React.Fragment>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  )}

                  {/* Markup Bands Overlay Results */}
                  {markupOverlay && showMarkupOverlay && (
                    <Grid item xs={12}>
                      <Card variant="outlined" sx={{ mt: 2 }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6">Markup Bands vs Price Bands Overlay</Typography>
                            <Button
                              size="small"
                              onClick={() => setShowMarkupOverlay(false)}
                              color="primary"
                            >
                              Hide
                            </Button>
                          </Box>

                          {/* Summary Info */}
                          <Grid container spacing={2} sx={{ mb: 3 }}>
                            <Grid item xs={6} md={3}>
                              <Typography variant="body2" color="text.secondary">Product Cost</Typography>
                              <Typography variant="h6">${markupOverlay.cost}</Typography>
                            </Grid>
                            <Grid item xs={6} md={3}>
                              <Typography variant="body2" color="text.secondary">Total Lead Time</Typography>
                              <Typography variant="h6">{markupOverlay.totalDays} days</Typography>
                            </Grid>
                            <Grid item xs={6} md={3}>
                              <Typography variant="body2" color="text.secondary">Earliest Ship</Typography>
                              <Typography variant="h6">{markupOverlay.earliestWaitDays} days</Typography>
                            </Grid>
                            <Grid item xs={6} md={3}>
                              <Typography variant="body2" color="text.secondary">Price Bands</Typography>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                {markupOverlay.bands.join(', ')}
                              </Typography>
                            </Grid>
                          </Grid>

                          {/* Overlay Table */}
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Tier</TableCell>
                                <TableCell align="right">Markup</TableCell>
                                <TableCell align="right">Theoretical Price</TableCell>
                                <TableCell align="right">Snapped Price</TableCell>
                                <TableCell align="right">Delta</TableCell>
                                <TableCell align="right">Final Margin</TableCell>
                                <TableCell align="right">Wait Window</TableCell>
                                <TableCell align="center">Status</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {markupOverlay.tiers.map((tier, index) => (
                                <TableRow key={index}>
                                  <TableCell>
                                    <Typography variant="body2" fontWeight="medium">{tier.name}</Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography variant="body2">{tier.markup}x</Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography variant="body2" color="text.secondary">
                                      ${tier.theoretical}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography variant="body1" fontWeight="medium">
                                      ${tier.snapped}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography
                                      variant="body2"
                                      color={tier.delta >= 0 ? 'success.main' : 'error.main'}
                                    >
                                      {tier.delta >= 0 ? '+' : ''}${tier.delta}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography
                                      variant="body2"
                                      color={tier.warning ? 'error' : 'text.primary'}
                                    >
                                      {(tier.mur * 100).toFixed(1)}%
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography variant="body2" color="text.secondary">
                                      {tier.waitWindowDays.startDay === 0 ? 'Ships now' :
                                       `${tier.waitWindowDays.startDay}-${tier.waitWindowDays.endDay} days`}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center">
                                    {tier.warning ? (
                                      <Chip size="small" label="Below Min" color="error" />
                                    ) : (
                                      <Chip size="small" label="OK" color="success" />
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>

                          {/* Insights */}
                          <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>Pricing Insights:</Typography>
                            <Box component="ul" sx={{ m: 0, pl: 2 }}>
                              <li>
                                <Typography variant="body2">
                                  Theoretical prices are calculated using pure markup multipliers
                                </Typography>
                              </li>
                              <li>
                                <Typography variant="body2">
                                  Snapped prices are aligned to your brand's psychological price bands
                                </Typography>
                              </li>
                              <li>
                                <Typography variant="body2">
                                  Positive delta = price bands allow higher prices than markup suggests
                                </Typography>
                              </li>
                              <li>
                                <Typography variant="body2">
                                  Negative delta = markup would exceed nearest price band
                                </Typography>
                              </li>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  )}
                </Grid>
              </AccordionDetails>
            </Accordion>

            <Typography variant="h6" sx={{ mb: 2 }}>
              Tier Configuration
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Configure your campaign tiers using the Enhanced Pricing Results above.
              Select tiers from the pricing breakdown table to automatically populate tier settings.
            </Typography>

            {/* Production & Constraints by Tier */}
            <TierProductionConfig
              tiers={availableTiers}
              tierProductionConfigs={tierProductionConfigs}
              onUpdateTierProduction={setTierProductionConfigs}
              productionTemplate={productionTemplate}
            />
          </Box>
        );
        
      case 3:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Campaign Story & Content
              </Typography>
            </Grid>

            {/* Core Narrative */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                1. Core Narrative
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Capture the headline and the full story merchants will use to position this drop.
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="content.storyTitle"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Story Title / Headline"
                    placeholder="e.g., 'Ocean Current Capsule – A Collab with Waveform Studios'"
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Story Content
              </Typography>
              <Controller
                name="content.storyContent"
                control={control}
                render={({ field }) => (
                  <RichTextEditor
                    value={field.value || ''}
                    onChange={field.onChange}
                    placeholder="Explain the inspiration, why the collection exists now, and the lifestyle it embodies (2–3 paragraphs)."
                  />
                )}
              />
            </Grid>

            {/* Design & Craft */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                2. Design & Craft
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Document the creative decisions, craftsmanship, and styling guidance that differentiate this campaign.
              </Typography>
            </Grid>

            <Grid item xs={12} md={4}>
              <Controller
                name="content.designProcess"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    multiline
                    rows={4}
                    label="Design Process"
                    placeholder="Highlight materials, construction choices, or collaborations that make this drop stand out."
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Controller
                name="content.lifestylePhilosophy"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    multiline
                    rows={4}
                    label="Lifestyle Philosophy"
                    placeholder="Describe the emotions or values this campaign represents (eco, performance, community, etc.)."
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Controller
                name="content.stylingNotes"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    multiline
                    rows={4}
                    label="Styling Notes"
                    placeholder="Offer practical styling tips to help marketing teams build lookbooks."
                  />
                )}
              />
            </Grid>

            {/* Audience & Positioning */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                3. Audience & Positioning
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Clarify who this campaign is for and set expectations for fulfillment timelines.
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="content.targetCustomer"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    multiline
                    rows={4}
                    label="Target Customer"
                    placeholder="Define demographics, style tribes, or use cases this drop serves."
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="content.productionTimeline"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Production Timeline"
                    placeholder="e.g., Made-to-order, ships in 6 weeks."
                  />
                )}
              />
            </Grid>

            {/* Visual & Proof Points */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                4. Visual & Proof Points
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Link the media and proof points that reinforce the story for both admin reviewers and storefront content.
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="content.heroMedia"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    multiline
                    rows={4}
                    label="Hero Imagery / Media"
                    placeholder="Provide URLs or asset references for hero images or videos."
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="content.socialProof"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    multiline
                    rows={4}
                    label="Testimonials & Social Proof"
                    placeholder="Share quotes, past performance stats, or references from prior drops."
                  />
                )}
              />
            </Grid>
          </Grid>
        );
        
      case 4:
        const formData = watch();
        return (
          <Box>
            <Typography variant="h6" sx={{ mb: 3 }}>
              Campaign Preview
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Card>
                  <CardContent>
                    <Typography variant="h5" sx={{ mb: 2 }}>
                      {formData.name || 'Untitled Campaign'}
                    </Typography>
                    <Divider sx={{ my: 2 }} />
                    
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Tiers ({formData.tiers?.length || 0})
                    </Typography>
                    
                    {formData.tiers?.map((tier, index) => (
                      <Card key={index} variant="outlined" sx={{ mb: 2 }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="h6">{tier.name}</Typography>
                            <Chip size="small" label={`Target: ${tier.maxReservationTarget}`} />
                          </Box>
                          
                          {tier.description && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                              {tier.description}
                            </Typography>
                          )}
                          
                          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <Chip
                              size="small"
                              label={`Reservation Opens: ${new Date(tier.reservationWindow?.startDate || tier.startDate).toLocaleDateString()}`}
                              variant="outlined"
                            />
                            <Chip
                              size="small"
                              label={`Reservation Closes: ${new Date(tier.reservationWindow?.endDate || tier.endDate).toLocaleDateString()}`}
                              variant="outlined"
                            />
                            {tier.delivery?.priorityLevel !== undefined && (
                              <Chip
                                size="small"
                                label={`Delivery: +${tier.delivery.priorityLevel} days after production`}
                                color="primary"
                                variant="outlined"
                              />
                            )}
                            {tier.variantId && selectedProduct && (
                              <Chip
                                size="small"
                                label={selectedProduct.variants?.find(v => v.id === tier.variantId)?.title || 'Variant'}
                                color="primary"
                                variant="outlined"
                              />
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    ))}
                    
                    <Divider sx={{ my: 3 }} />

                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Campaign Story
                    </Typography>

                    {formData.content?.storyTitle && (
                      <Typography variant="subtitle1" sx={{ mb: 1 }}>
                        {formData.content.storyTitle}
                      </Typography>
                    )}

                    {formData.content?.storyContent && (
                      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                        {formData.content.storyContent}
                      </Typography>
                    )}

                    <Grid container spacing={2}>
                      {(formData.content?.designProcess || formData.content?.lifestylePhilosophy || formData.content?.stylingNotes) && (
                        <Grid item xs={12}>
                          <Typography variant="subtitle1" sx={{ mt: 2 }}>
                            Design & Craft
                          </Typography>
                        </Grid>
                      )}
                      {formData.content?.designProcess && (
                        <Grid item xs={12} md={4}>
                          <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                            Design Process
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {formData.content.designProcess}
                          </Typography>
                        </Grid>
                      )}
                      {formData.content?.lifestylePhilosophy && (
                        <Grid item xs={12} md={4}>
                          <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                            Lifestyle Philosophy
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {formData.content.lifestylePhilosophy}
                          </Typography>
                        </Grid>
                      )}
                      {formData.content?.stylingNotes && (
                        <Grid item xs={12} md={4}>
                          <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                            Styling Notes
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {formData.content.stylingNotes}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>

                    {(formData.content?.targetCustomer || formData.content?.productionTimeline) && (
                      <>
                        <Typography variant="subtitle1" sx={{ mt: 3 }}>
                          Audience & Positioning
                        </Typography>
                        <Grid container spacing={2}>
                          {formData.content?.targetCustomer && (
                            <Grid item xs={12} md={6}>
                              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                                Target Customer
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {formData.content.targetCustomer}
                              </Typography>
                            </Grid>
                          )}
                          {formData.content?.productionTimeline && (
                            <Grid item xs={12} md={6}>
                              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                                Production Timeline
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {formData.content.productionTimeline}
                              </Typography>
                            </Grid>
                          )}
                        </Grid>
                      </>
                    )}

                    {(formData.content?.heroMedia || formData.content?.socialProof) && (
                      <>
                        <Typography variant="subtitle1" sx={{ mt: 3 }}>
                          Visual & Proof Points
                        </Typography>
                        <Grid container spacing={2}>
                          {formData.content?.heroMedia && (
                            <Grid item xs={12} md={6}>
                              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                                Hero Imagery / Media
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {formData.content.heroMedia}
                              </Typography>
                            </Grid>
                          )}
                          {formData.content?.socialProof && (
                            <Grid item xs={12} md={6}>
                              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                                Testimonials & Social Proof
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {formData.content.socialProof}
                              </Typography>
                            </Grid>
                          )}
                        </Grid>
                      </>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Campaign Settings
                    </Typography>
                    
                    <List dense>
                      <ListItem>
                        <ListItemText
                          primary="Auto-advance tiers"
                          secondary={formData.settings?.autoAdvanceTiers ? 'Enabled' : 'Disabled'}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Milestone notifications"
                          secondary={formData.settings?.notifyOnMilestones ? 'Enabled' : 'Disabled'}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Allow backorders"
                          secondary={formData.settings?.allowBackorders ? 'Yes' : 'No'}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Max reservations per customer"
                          secondary={formData.settings?.maxReservationsPerCustomer || 'Unlimited'}
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        );
        
      default:
        return null;
    }
  };
  
  if (loading) {
    return <LoadingSpinner />;
  }
  
  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={700}>
          {isEditing ? 'Edit Campaign' : 'Create New Campaign'}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {isEditing ? 'Update your campaign settings' : 'Set up a new reservation campaign'}
        </Typography>
      </Box>
      
      {/* Brand selection */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Choose Brand
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select the brand environment this campaign belongs to.
          </Typography>
          <Autocomplete
            options={brandOptions}
            loading={brandLoading}
            value={selectedBrand}
            getOptionLabel={(option) => option?.name ?? ''}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(_, value) => setSelectedBrand(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Brand"
                placeholder="Select brand"
                helperText={isEditing ? undefined : 'Brand selection is required before continuing.'}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {brandLoading ? <CircularProgress color="inherit" size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          {brandError && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {brandError}
            </Alert>
          )}
        </CardContent>
      </Card>

      {!brandReady && !isEditing && (
        <Alert severity="info" sx={{ mb: 4 }}>
          Select a brand to unlock the campaign builder.
        </Alert>
      )}

      {brandReady && (
        <>
          {/* Stepper */}
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>

              {/* Product Progress Indicator - Show on Step 3 */}
              {activeStep === 2 && selectedLooks.length > 0 && (
                <Alert severity="info" sx={{ mb: 3 }}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Box flex={1}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Multi-Product Configuration Progress
                      </Typography>
                      <Typography variant="body2">
                        Configure pricing and tiers for each product individually
                      </Typography>
                    </Box>
                    <Box display="flex" gap={1}>
                      {selectedLooks.map((look, idx) => (
                        <Chip
                          key={look.lookId}
                          label={`${idx + 1}`}
                          size="small"
                          color={idx === currentProductIndex ? 'primary' : idx < currentProductIndex ? 'success' : 'default'}
                          variant={idx === currentProductIndex ? 'filled' : 'outlined'}
                        />
                      ))}
                    </Box>
                  </Box>
                </Alert>
              )}

              {(selectedBrand || brandIdValue) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Working in brand:
                  </Typography>
                  <Chip
                    label={
                      selectedBrand?.name ||
                      brandOptions.find((brand) => brand.id === brandIdValue)?.name ||
                      brandIdValue
                    }
                    color="primary"
                    size="small"
                  />
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Step content */}
          <Card>
            <CardContent sx={{ minHeight: 400 }}>
              {getStepContent(activeStep)}
            </CardContent>

            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Button
                  disabled={activeStep === 0 && currentProductIndex === 0}
                  onClick={handleBack}
                >
                  {activeStep === 2 && currentProductIndex > 0
                    ? `Previous Product (${currentProductIndex}/${selectedLooks.length})`
                    : 'Back'}
                </Button>

                <Box sx={{ display: 'flex', gap: 1 }}>
                  {activeStep === steps.length - 1 ? (
                    <>
                      <Button
                        variant="outlined"
                        startIcon={<SaveIcon />}
                        onClick={handleSubmit(onSubmit)}
                        disabled={loading}
                      >
                        {isEditing ? 'Update' : 'Save Draft'}
                      </Button>
                      <Button
                        variant="contained"
                        startIcon={<LaunchIcon />}
                        onClick={handleSubmit(handleLaunchCampaign)}
                        disabled={loading}
                      >
                        {isEditing ? 'Update & Launch' : 'Create & Launch'}
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="contained"
                      onClick={handleNext}
                      disabled={loading}
                    >
                      {activeStep === 2 && selectedLooks.length > 0 && currentProductIndex < selectedLooks.length - 1
                        ? `Next Product (${currentProductIndex + 2}/${selectedLooks.length})`
                        : 'Next'}
                    </Button>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
};

export default CampaignBuilderPage;
